import { ApiError, apiRequest, type FetchFn, type RequestOptions } from './client';

/** The token is gone and signing in again did not help: the resident must reopen the app (spec §5.4). */
export class SessionLostError extends Error {
  constructor() {
    super('Session lost');
    this.name = 'SessionLostError';
  }
}

export interface Session {
  request<T>(path: string, options?: Omit<RequestOptions, 'token'>): Promise<T>;
}

interface SessionDeps {
  fetchFn: FetchFn;
  baseUrl: string;
  token: string;
  /** Signs in again with the current initData; resolves with a fresh token. */
  relogin: () => Promise<string>;
  /** Called after every successful request: usage metrics count what was created (metrics.ts). */
  onSuccess?: (path: string, options: Omit<RequestOptions, 'token'>) => void;
  /** Called when mobile-api refuses a request with an error code: metrics count failed orders. */
  onFailure?: (path: string, options: Omit<RequestOptions, 'token'>, error: ApiError) => void;
}

const isAuthError = (error: unknown) =>
  error instanceof ApiError && (error.code === 'UNAUTHENTICATED' || error.code === 'INVALID_TOKEN');

/** Authenticated access to mobile-api. The token lives only in this closure (spec §3). */
export function createSession({ fetchFn, baseUrl, token, relogin, onSuccess, onFailure }: SessionDeps): Session {
  let current = token;
  let pending: Promise<string> | null = null;

  const refresh = (rejected: string): Promise<string> => {
    if (current !== rejected) return Promise.resolve(current);
    pending ??= relogin()
      .then((fresh) => (current = fresh))
      .finally(() => {
        pending = null;
      });
    return pending;
  };

  return {
    async request<T>(path: string, options: Omit<RequestOptions, 'token'> = {}) {
      const done = (result: T) => {
        try {
          onSuccess?.(path, options);
        } catch {
          // Metrics must never break a request.
        }
        return result;
      };
      const failed = (error: unknown) => {
        if (error instanceof ApiError && !isAuthError(error)) {
          try {
            onFailure?.(path, options, error);
          } catch {
            // Metrics must never break a request.
          }
        }
        return error;
      };
      const used = current;
      try {
        return done(await apiRequest<T>(fetchFn, baseUrl, path, { ...options, token: used }));
      } catch (error) {
        if (!isAuthError(error)) throw failed(error);
      }
      let fresh: string;
      try {
        fresh = await refresh(used);
      } catch {
        throw new SessionLostError();
      }
      try {
        return done(await apiRequest<T>(fetchFn, baseUrl, path, { ...options, token: fresh }));
      } catch (error) {
        if (isAuthError(error)) throw new SessionLostError();
        throw failed(error);
      }
    },
  };
}
